import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useLanguage } from "../contexts/LanguageContext";

export default function Cart() {
  const { items, removeItem, updateQuantity, total } = useCart();
  const { lang, t } = useLanguage();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background pt-20 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <ShoppingBag size={48} strokeWidth={1} className="text-foreground/20 mx-auto mb-6" />
          <h2 className="text-2xl font-heading font-bold tracking-tighter text-foreground mb-3">{t("cart.empty")}</h2>
          <p className="text-foreground/50 mb-8">{t("cart.empty.desc")}</p>
          <Link
            to="/shop"
            data-testid="continue-shopping-btn"
            className="inline-flex items-center gap-2 px-8 py-4 bg-foreground text-background text-sm font-mono tracking-widest uppercase rounded-sm hover:bg-foreground/90 transition-all"
          >
            {t("cart.continue")}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-28">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl font-heading font-bold tracking-tighter text-foreground mb-10"
        >
          {t("cart.title")}
          <span className="text-foreground/30 text-2xl ml-3 font-light">({items.length} {t("cart.items")})</span>
        </motion.h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item, i) => {
              const name = lang === "de" ? item.product.name_de : item.product.name_en;
              const img = item.product.images?.[0] || "";
              return (
                <motion.div
                  key={item.product.id}
                  data-testid={`cart-item-${item.product.id}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="flex gap-4 p-4 bg-surface border border-border rounded-sm"
                >
                  <Link to={`/shop/${item.product.id}`} className="flex-shrink-0">
                    <div className="w-20 h-20 rounded-sm overflow-hidden bg-background">
                      <img src={img} alt={name} className="w-full h-full object-cover" />
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/shop/${item.product.id}`}>
                      <h3 className="text-sm font-heading font-medium text-foreground hover:opacity-70 transition-opacity">{name}</h3>
                    </Link>
                    <p className="text-xs text-foreground/40 font-mono mt-0.5">{item.product.material}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <div className="flex items-center border border-border rounded-sm overflow-hidden">
                        <button
                          data-testid={`cart-dec-${item.product.id}`}
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="px-2 py-1 text-foreground/50 hover:text-foreground hover:bg-foreground/5 transition-colors"
                        >
                          <Minus size={12} strokeWidth={1.5} />
                        </button>
                        <span className="px-3 py-1 text-sm font-mono text-foreground border-x border-border">{item.quantity}</span>
                        <button
                          data-testid={`cart-inc-${item.product.id}`}
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="px-2 py-1 text-foreground/50 hover:text-foreground hover:bg-foreground/5 transition-colors"
                        >
                          <Plus size={12} strokeWidth={1.5} />
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${item.product.id}`}
                        onClick={() => removeItem(item.product.id)}
                        className="text-foreground/30 hover:text-destructive transition-colors"
                      >
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-mono text-foreground">€{(item.product.price * item.quantity).toFixed(2)}</p>
                    <p className="text-xs text-foreground/30 font-mono mt-1">€{item.product.price.toFixed(2)} / {lang === "de" ? "Stk." : "ea."}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="sticky top-28 bg-surface border border-border rounded-sm p-6"
            >
              <h3 className="text-sm font-mono tracking-widest uppercase text-foreground/50 mb-6">{t("checkout.summary")}</h3>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-foreground/60">{t("cart.subtotal")}</span>
                  <span className="font-mono text-foreground">€{total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-foreground/60">{t("cart.shipping")}</span>
                  <span className="font-mono text-foreground">{t("cart.free_shipping")}</span>
                </div>
                <div className="border-t border-border pt-3 flex justify-between">
                  <span className="font-medium text-foreground">{t("cart.total")}</span>
                  <span className="font-mono font-bold text-foreground text-lg">€{total.toFixed(2)}</span>
                </div>
              </div>

              <button
                data-testid="checkout-btn"
                onClick={() => navigate("/checkout")}
                className="w-full py-4 bg-foreground text-background text-sm font-mono tracking-widest uppercase rounded-sm hover:bg-foreground/90 transition-all duration-300"
              >
                {t("cart.checkout")}
              </button>
              <Link
                to="/shop"
                data-testid="continue-shopping-btn"
                className="block text-center mt-3 text-xs text-foreground/40 hover:text-foreground transition-colors font-mono tracking-wider"
              >
                {t("cart.continue")}
              </Link>
            </motion.div>
          </div>
        </div>
        <div className="pb-24" />
      </div>
    </div>
  );
}
